<template>
  <div class="thermo-page">
    <!-- Панель управления -->
    <div class="controls-row">
      <!-- Левая камера -->
      <div class="control-card">
        <h3>🧪 Левая камера</h3>
        <label>Газ:</label>
        <select v-model="leftGas">
          <option v-for="gas in gases" :key="gas.name" :value="gas">{{ gas.name }}</option>
        </select>
        <label>Молекул: <strong>{{ leftCount }}</strong></label>
        <div class="btn-row">
          <button @click="addMolecules('left', 10)">+10</button>
          <button @click="removeMolecules('left', 10)">−10</button>
        </div>
        <div class="btn-row">
          <button @click="heat('left')" class="heat-btn">🔥 Нагреть</button>
          <button @click="cool('left')" class="cool-btn">❄️ Охладить</button>
        </div>
        <label class="small-label">
          <input type="checkbox" v-model="leftHeaterOn" /> Нагреватель снизу
        </label>
      </div>

      <!-- Центр — поршень и глобальные -->
      <div class="control-card piston-card">
        <h3>⚙️ Поршень</h3>
        <button @click="pistonLocked = !pistonLocked" :class="{ active: pistonLocked }">
          {{ pistonLocked ? '🔒 Закреплён' : '🔓 Свободен' }}
        </button>
        <button @click="pistonConducting = !pistonConducting" :class="{ active: pistonConducting }">
          {{ pistonConducting ? '🔥 Проводит' : '❄️ Изолирует' }}
        </button>
        <button @click="resetPiston">⏏ Сбросить</button>
        <div class="global-controls">
          <button @click="toggleRun" class="run-btn">
            {{ running ? '⏸ Стоп' : '▶ Пуск' }}
          </button>
          <button @click="resetAll" class="reset-btn">↺ Сброс всего</button>
        </div>
      </div>

      <!-- Правая камера -->
      <div class="control-card">
        <h3>🧪 Правая камера</h3>
        <label>Газ:</label>
        <select v-model="rightGas">
          <option v-for="gas in gases" :key="gas.name" :value="gas">{{ gas.name }}</option>
        </select>
        <label>Молекул: <strong>{{ rightCount }}</strong></label>
        <div class="btn-row">
          <button @click="addMolecules('right', 10)">+10</button>
          <button @click="removeMolecules('right', 10)">−10</button>
        </div>
        <div class="btn-row">
          <button @click="heat('right')" class="heat-btn">🔥 Нагреть</button>
          <button @click="cool('right')" class="cool-btn">❄️ Охладить</button>
        </div>
        <label class="small-label">
          <input type="checkbox" v-model="rightHeaterOn" /> Нагреватель снизу
        </label>
      </div>
    </div>

    <!-- Основной Canvas -->
    <div class="canvas-wrapper" ref="wrapper">
      <canvas ref="mainCanvas" />
    </div>

    <!-- Статистика и графики -->
    <div class="stats-row">
      <div class="stat-card">
        <h4>📊 Левая камера</h4>
        <div class="stat-line"><span class="label">P (давление):</span> <span class="value">{{ fmt(leftPressure) }} атм</span></div>
        <div class="stat-line"><span class="label">T (температура):</span> <span class="value">{{ fmt(leftTemp) }} K</span></div>
        <div class="stat-line"><span class="label">V (объём):</span> <span class="value">{{ fmt(leftVolume) }} px²</span></div>
        <div class="stat-line"><span class="label">N (молекул):</span> <span class="value">{{ leftCount }}</span></div>
        <div class="stat-line"><span class="label">v̄ (ср. скорость):</span> <span class="value">{{ fmt(leftAvgSpeed) }} px/кадр</span></div>
        <div class="stat-line"><span class="label">Eₖᵢₙ (энергия):</span> <span class="value">{{ fmt(leftEnergy) }}</span></div>
      </div>

      <div class="graph-card">
        <h4>📈 P-V диаграмма</h4>
        <canvas ref="pvCanvas" width="220" height="160" />
      </div>

      <div class="graph-card">
        <h4>📊 Распределение скоростей</h4>
        <canvas ref="histCanvas" width="220" height="160" />
      </div>

      <div class="stat-card">
        <h4>📊 Правая камера</h4>
        <div class="stat-line"><span class="label">P (давление):</span> <span class="value">{{ fmt(rightPressure) }} атм</span></div>
        <div class="stat-line"><span class="label">T (температура):</span> <span class="value">{{ fmt(rightTemp) }} K</span></div>
        <div class="stat-line"><span class="label">V (объём):</span> <span class="value">{{ fmt(rightVolume) }} px²</span></div>
        <div class="stat-line"><span class="label">N (молекул):</span> <span class="value">{{ rightCount }}</span></div>
        <div class="stat-line"><span class="label">v̄ (ср. скорость):</span> <span class="value">{{ fmt(rightAvgSpeed) }} px/кадр</span></div>
        <div class="stat-line"><span class="label">Eₖᵢₙ (энергия):</span> <span class="value">{{ fmt(rightEnergy) }}</span></div>
      </div>
    </div>

    <div class="theory-box">
      <h4>🔬 Физика процессов</h4>
      <p>
        <strong>Давление</strong> возникает от ударов молекул о стенки и поршень: <em>P = Σ(2·m·|vₓ|) / (S·Δt)</em>.
        <strong>Температура</strong> пропорциональна средней кинетической энергии: <em>T = ⟨½mv²⟩ · 10</em>.
        <strong>Теплообмен</strong> через поршень происходит при столкновениях молекул с его поверхностью — чем больше молекул и чем быстрее они движутся, тем интенсивнее обмен.
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';

/* ===================== КОНФИГУРАЦИЯ ===================== */
const CANVAS_H = 420;
const WALL = 3;
const PISTON_W = 10;
const SUB_STEPS = 3;
const TEMP_SCALE = 10;      // T[K] = E_kin * TEMP_SCALE
const PRESSURE_SCALE = 250; // P[атм] = (E_total / Area) * PRESSURE_SCALE
const PISTON_MASS = 3000;

const gases = [
  { name: 'Гелий (He)', mass: 4,  radius: 3.0, color: '#FFD700' },
  { name: 'Неон (Ne)',  mass: 20, radius: 3.8, color: '#FF8C00' },
  { name: 'Аргон (Ar)', mass: 40, radius: 4.5, color: '#FF4500' },
  { name: 'Азот (N₂)',  mass: 28, radius: 4.0, color: '#00BFFF' },
  { name: 'Кислород (O₂)', mass: 32, radius: 4.2, color: '#1E90FF' },
];

/* ===================== РЕФЫ ===================== */
const wrapper = ref(null);
const mainCanvas = ref(null);
const pvCanvas = ref(null);
const histCanvas = ref(null);

const leftGas = ref(gases[3]);
const rightGas = ref(gases[0]);
const pistonLocked = ref(false);
const pistonConducting = ref(true);
const leftHeaterOn = ref(false);
const rightHeaterOn = ref(false);
const running = ref(false);

// Основные массивы молекул (не реактивные — для производительности)
let leftMols = [];
let rightMols = [];
let piston = { x: 0, vx: 0 };
let canvasW = 960;
let animId = null;
let frameCount = 0;

// История для графиков
let pvHistory = [];

// Триггер пересчёта computed-статистики
const tick = ref(0);

/* ===================== ИНИЦИАЛИЗАЦИЯ ===================== */
function initCanvas() {
  const c = mainCanvas.value;
  const w = wrapper.value;
  canvasW = w.clientWidth;
  c.width = canvasW;
  c.height = CANVAS_H;
}

function resetAll() {
  leftMols = [];
  rightMols = [];
  piston.x = canvasW / 2;
  piston.vx = 0;
  pvHistory = [];
  frameCount = 0;
  for (let i = 0; i < 60; i++) spawnMol('left', 300, false);
  for (let i = 0; i < 60; i++) spawnMol('right', 300, false);
  tick.value++;
}

function resetPiston() {
  piston.x = canvasW / 2;
  piston.vx = 0;
  tick.value++;
}

/* ===================== МОЛЕКУЛЫ ===================== */
function gauss() {
  let u = 0, v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

function spawnMol(side, T = 300, trigger = true) {
  const isL = side === 'left';
  const gas = isL ? leftGas.value : rightGas.value;
  const arr = isL ? leftMols : rightMols;
  const minX = isL ? WALL : piston.x + PISTON_W;
  const maxX = isL ? piston.x : canvasW - WALL;
  const W = maxX - minX;
  const H = CANVAS_H - 2 * WALL;
  const r = gas.radius;

  const sigma = Math.sqrt(T / (gas.mass * TEMP_SCALE));

  let x, y, safe = 0;
  do {
    x = minX + r + Math.random() * (W - 2 * r);
    y = WALL + r + Math.random() * (H - 2 * r);
    safe++;
  } while (safe < 20 && overlaps(x, y, r, arr));

  arr.push({
    x, y,
    vx: gauss() * sigma,
    vy: gauss() * sigma,
    mass: gas.mass,
    radius: r,
    color: gas.color,
  });
  if (trigger) tick.value++;
}

function overlaps(x, y, r, arr) {
  for (const m of arr) {
    const dx = m.x - x, dy = m.y - y;
    if (dx * dx + dy * dy < (m.radius + r + 1) ** 2) return true;
  }
  return false;
}

function addMolecules(side, n) {
  for (let i = 0; i < n; i++) spawnMol(side);
}

function removeMolecules(side, n) {
  const arr = side === 'left' ? leftMols : rightMols;
  for (let i = 0; i < n && arr.length > 0; i++) arr.pop();
  tick.value++;
}

function heat(side) {
  const arr = side === 'left' ? leftMols : rightMols;
  const f = 1.25;
  arr.forEach(m => { m.vx *= f; m.vy *= f; });
  tick.value++;
}

function cool(side) {
  const arr = side === 'left' ? leftMols : rightMols;
  const f = 0.82;
  arr.forEach(m => { m.vx *= f; m.vy *= f; });
  tick.value++;
}

/* ===================== ФИЗИКА ===================== */
function updatePhysics() {
  const dt = 1 / SUB_STEPS;
  for (let s = 0; s < SUB_STEPS; s++) {
    moveMols(leftMols, 'left', dt);
    moveMols(rightMols, 'right', dt);
    collideMols(leftMols);
    collideMols(rightMols);
    if (!pistonLocked.value) updatePiston(dt);
    else handleLockedPiston();
  }
}

function moveMols(arr, side, dt) {
  const isL = side === 'left';
  const minX = isL ? WALL : piston.x + PISTON_W;
  const maxX = isL ? piston.x : canvasW - WALL;
  const minY = WALL;
  const maxY = CANVAS_H - WALL;
  const heaterOn = isL ? leftHeaterOn.value : rightHeaterOn.value;

  for (const m of arr) {
    m.x += m.vx * dt;
    m.y += m.vy * dt;

    // Левая/правая стенка
    if (m.x < minX + m.radius) {
      m.x = minX + m.radius;
      m.vx = Math.abs(m.vx);
    }
    if (m.x > maxX - m.radius) {
      m.x = maxX - m.radius;
      m.vx = -Math.abs(m.vx);
    }

    // Верхняя/нижняя стенка
    if (m.y < minY + m.radius) {
      m.y = minY + m.radius;
      m.vy = Math.abs(m.vy);
    }
    if (m.y > maxY - m.radius) {
      m.y = maxY - m.radius;
      // Нагреватель снизу
      if (heaterOn) {
        const boost = 1.0 + 0.08 * Math.random();
        m.vx *= boost;
        m.vy *= boost;
        m.vy = -Math.abs(m.vy) * 0.95; // чуть меньше отскок для нагрева
      } else {
        m.vy = -Math.abs(m.vy);
      }
    }
  }
}

function updatePiston(dt) {
  // --- Столкновения с левой стороны ---
  for (const m of leftMols) {
    if (m.x + m.radius > piston.x) {
      const vRel = m.vx - piston.vx;
      if (vRel > 0) {
        // Упругое столкновение 1D
        const vp = (piston.vx * (PISTON_MASS - m.mass) + 2 * m.mass * m.vx) / (PISTON_MASS + m.mass);
        const vm = (m.vx * (m.mass - PISTON_MASS) + 2 * PISTON_MASS * piston.vx) / (PISTON_MASS + m.mass);
        piston.vx = vp;
        m.vx = vm;
        m.x = piston.x - m.radius - 0.5;

        if (pistonConducting.value) thermalize(m, 'left');
      }
    }
  }

  // --- Столкновения с правой стороны ---
  for (const m of rightMols) {
    if (m.x - m.radius < piston.x + PISTON_W) {
      const vRel = piston.vx - m.vx; // приближение?
      if (vRel > 0) { // поршень движется быстрее молекулы навстречу
        const vp = (piston.vx * (PISTON_MASS - m.mass) + 2 * m.mass * m.vx) / (PISTON_MASS + m.mass);
        const vm = (m.vx * (m.mass - PISTON_MASS) + 2 * PISTON_MASS * piston.vx) / (PISTON_MASS + m.mass);
        piston.vx = vp;
        m.vx = vm;
        m.x = piston.x + PISTON_W + m.radius + 0.5;

        if (pistonConducting.value) thermalize(m, 'right');
      }
    }
  }

  piston.x += piston.vx * dt;
  piston.vx *= 0.998; // слабое трение

  // Ограничители
  const minX = 80;
  const maxX = canvasW - 80;
  if (piston.x < minX) { piston.x = minX; piston.vx = Math.abs(piston.vx) * 0.3; }
  if (piston.x > maxX) { piston.x = maxX; piston.vx = -Math.abs(piston.vx) * 0.3; }
}

function handleLockedPiston() {
  // Даже закреплённый поршень может обмениваться теплом
  if (!pistonConducting.value) return;
  for (const m of leftMols) {
    if (m.x + m.radius > piston.x) {
      const vRel = m.vx;
      if (vRel > 0) {
        m.vx = -m.vx * 0.98;
        m.x = piston.x - m.radius;
        thermalize(m, 'left');
      }
    }
  }
  for (const m of rightMols) {
    if (m.x - m.radius < piston.x + PISTON_W) {
      const vRel = -m.vx;
      if (vRel > 0) {
        m.vx = -m.vx * 0.98;
        m.x = piston.x + PISTON_W + m.radius;
        thermalize(m, 'right');
      }
    }
  }
}

function thermalize(mol, side) {
  // Молекула обменивается энергией с "температурой поверхности поршня"
  // Чем выше теплопроводность, тем сильнее обмен
  const otherT = side === 'left' ? rightTemp.value : leftTemp.value;
  const thisT  = side === 'left' ? leftTemp.value : rightTemp.value;
  if (Math.abs(thisT - otherT) < 0.5) return;

  const targetT = (thisT + otherT) * 0.5;
  const targetE = targetT / TEMP_SCALE;
  const curE = 0.5 * mol.mass * (mol.vx * mol.vx + mol.vy * mol.vy);
  if (curE <= 0) return;

  const alpha = 0.08; // коэффициент аккомодации
  const newE = curE + alpha * (targetE - curE);
  const scale = Math.sqrt(Math.max(1e-6, newE) / curE);
  mol.vx *= scale;
  mol.vy *= scale;
}

function collideMols(arr) {
  for (let i = 0; i < arr.length; i++) {
    for (let j = i + 1; j < arr.length; j++) {
      const a = arr[i], b = arr[j];
      const dx = b.x - a.x, dy = b.y - a.y;
      const distSq = dx * dx + dy * dy;
      const minD = a.radius + b.radius;
      if (distSq >= minD * minD || distSq < 1e-6) continue;

      const dist = Math.sqrt(distSq);
      const nx = dx / dist, ny = dy / dist;

      const dvx = a.vx - b.vx, dvy = a.vy - b.vy;
      const velN = dvx * nx + dvy * ny;
      if (velN > 0) continue;

      const impulse = 2 * velN / (a.mass + b.mass);
      a.vx -= impulse * b.mass * nx;
      a.vy -= impulse * b.mass * ny;
      b.vx += impulse * a.mass * nx;
      b.vy += impulse * a.mass * ny;

      const overlap = minD - dist;
      a.x -= nx * overlap * 0.5;
      a.y -= ny * overlap * 0.5;
      b.x += nx * overlap * 0.5;
      b.y += ny * overlap * 0.5;
    }
  }
}

/* ===================== СТАТИСТИКА ===================== */
function totalKE(arr) {
  let s = 0;
  for (const m of arr) s += 0.5 * m.mass * (m.vx * m.vx + m.vy * m.vy);
  return s;
}

const leftCount = computed(() => { tick.value; return leftMols.length; });
const rightCount = computed(() => { tick.value; return rightMols.length; });

const leftTemp = computed(() => {
  tick.value;
  if (leftMols.length === 0) return 0;
  return totalKE(leftMols) / leftMols.length * TEMP_SCALE;
});
const rightTemp = computed(() => {
  tick.value;
  if (rightMols.length === 0) return 0;
  return totalKE(rightMols) / rightMols.length * TEMP_SCALE;
});

const leftVolume = computed(() => { tick.value; return Math.max(1, (piston.x - WALL) * (CANVAS_H - 2 * WALL)); });
const rightVolume = computed(() => { tick.value; return Math.max(1, (canvasW - piston.x - PISTON_W - WALL) * (CANVAS_H - 2 * WALL)); });

const leftPressure = computed(() => {
  tick.value;
  const V = leftVolume.value;
  if (V <= 0 || leftMols.length === 0) return 0;
  return totalKE(leftMols) / V * PRESSURE_SCALE;
});
const rightPressure = computed(() => {
  tick.value;
  const V = rightVolume.value;
  if (V <= 0 || rightMols.length === 0) return 0;
  return totalKE(rightMols) / V * PRESSURE_SCALE;
});

const leftAvgSpeed = computed(() => {
  tick.value;
  if (leftMols.length === 0) return 0;
  let s = 0;
  for (const m of leftMols) s += Math.sqrt(m.vx * m.vx + m.vy * m.vy);
  return s / leftMols.length;
});
const rightAvgSpeed = computed(() => {
  tick.value;
  if (rightMols.length === 0) return 0;
  let s = 0;
  for (const m of rightMols) s += Math.sqrt(m.vx * m.vx + m.vy * m.vy);
  return s / rightMols.length;
});

const leftEnergy = computed(() => { tick.value; return totalKE(leftMols); });
const rightEnergy = computed(() => { tick.value; return totalKE(rightMols); });

function fmt(n) { return Number(n).toFixed(n < 10 ? 2 : n < 100 ? 1 : 0); }

/* ===================== ОТРИСОВКА ===================== */
function draw() {
  const ctx = mainCanvas.value.getContext('2d');
  ctx.clearRect(0, 0, canvasW, CANVAS_H);

  // Фон
  ctx.fillStyle = '#0b0f1a';
  ctx.fillRect(0, 0, canvasW, CANVAS_H);

  // Сетка
  ctx.strokeStyle = 'rgba(100,150,255,0.06)';
  ctx.lineWidth = 1;
  for (let x = 0; x < canvasW; x += 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, CANVAS_H); ctx.stroke(); }
  for (let y = 0; y < CANVAS_H; y += 40) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvasW, y); ctx.stroke(); }

  // Молекулы
  drawMols(ctx, leftMols);
  drawMols(ctx, rightMols);

  // Стенки
  ctx.fillStyle = '#6c7a89';
  ctx.fillRect(0, 0, WALL, CANVAS_H);
  ctx.fillRect(canvasW - WALL, 0, WALL, CANVAS_H);
  ctx.fillRect(0, 0, canvasW, WALL);
  ctx.fillRect(0, CANVAS_H - WALL, canvasW, WALL);

  // Поршень
  const pc = pistonConducting.value ? '#c0392b' : '#2980b9';
  ctx.fillStyle = pc;
  ctx.fillRect(piston.x, WALL, PISTON_W, CANVAS_H - 2 * WALL);
  // Ободок поршня
  ctx.strokeStyle = '#ecf0f1';
  ctx.lineWidth = 1;
  ctx.strokeRect(piston.x, WALL, PISTON_W, CANVAS_H - 2 * WALL);
  // Индикатор движения
  if (Math.abs(piston.vx) > 0.05) {
    ctx.fillStyle = `rgba(255,255,255,${Math.min(0.25, Math.abs(piston.vx) * 0.03)})`;
    ctx.fillRect(piston.x - 2, WALL, PISTON_W + 4, CANVAS_H - 2 * WALL);
  }

  // Нагреватели
  if (leftHeaterOn.value) {
    ctx.fillStyle = 'rgba(231,76,60,0.4)';
    ctx.fillRect(WALL, CANVAS_H - WALL - 6, piston.x - WALL, 6);
  }
  if (rightHeaterOn.value) {
    ctx.fillStyle = 'rgba(231,76,60,0.4)';
    ctx.fillRect(piston.x + PISTON_W, CANVAS_H - WALL - 6, canvasW - piston.x - PISTON_W - WALL, 6);
  }

  // Подписи
  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.font = '12px Segoe UI, sans-serif';
  ctx.fillText(`P=${fmt(leftPressure.value)}  T=${fmt(leftTemp.value)}K`, WALL + 6, 18);
  ctx.fillText(`P=${fmt(rightPressure.value)}  T=${fmt(rightTemp.value)}K`, piston.x + PISTON_W + 6, 18);
}

function drawMols(ctx, arr) {
  for (const m of arr) {
    const spd = Math.sqrt(m.vx * m.vx + m.vy * m.vy);
    const t = Math.min(1, spd / 7);
    const r = Math.floor(100 + t * 155);
    const g = Math.floor(50 + t * 100);
    const b = Math.floor(200 - t * 180);

    // Свечение
    if (t > 0.4) {
      ctx.beginPath();
      ctx.arc(m.x, m.y, m.radius + 3, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${r},${g},${b},0.15)`;
      ctx.fill();
    }

    ctx.beginPath();
    ctx.arc(m.x, m.y, m.radius, 0, Math.PI * 2);
    ctx.fillStyle = `rgb(${r},${g},${b})`;
    ctx.fill();
  }
}

function drawPV() {
  const ctx = pvCanvas.value.getContext('2d');
  ctx.clearRect(0, 0, 220, 160);
  ctx.fillStyle = '#0f1525';
  ctx.fillRect(0, 0, 220, 160);

  // Собираем точки
  if (frameCount % 5 === 0) {
    pvHistory.push({
      l: { p: leftPressure.value, v: leftVolume.value },
      r: { p: rightPressure.value, v: rightVolume.value },
    });
    if (pvHistory.length > 80) pvHistory.shift();
  }

  // Масштабы
  const maxV = Math.max(50000, ...pvHistory.map(h => Math.max(h.l.v, h.r.v))) * 1.1;
  const maxP = Math.max(3, ...pvHistory.map(h => Math.max(h.l.p, h.r.p))) * 1.1;

  // Оси
  ctx.strokeStyle = 'rgba(255,255,255,0.2)';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(30, 140); ctx.lineTo(210, 140); ctx.stroke(); // V
  ctx.beginPath(); ctx.moveTo(30, 140); ctx.lineTo(30, 10); ctx.stroke();   // P

  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.font = '10px sans-serif';
  ctx.fillText('V', 205, 153);
  ctx.fillText('P', 12, 15);

  // Левая камера (жёлто-оранжевая)
  ctx.strokeStyle = '#f39c12';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  for (let i = 0; i < pvHistory.length; i++) {
    const px = 30 + (pvHistory[i].l.v / maxV) * 170;
    const py = 140 - (pvHistory[i].l.p / maxP) * 120;
    if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
  }
  ctx.stroke();

  // Правая камера (синяя)
  ctx.strokeStyle = '#3498db';
  ctx.beginPath();
  for (let i = 0; i < pvHistory.length; i++) {
    const px = 30 + (pvHistory[i].r.v / maxV) * 170;
    const py = 140 - (pvHistory[i].r.p / maxP) * 120;
    if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
  }
  ctx.stroke();

  // Легенда
  ctx.fillStyle = '#f39c12'; ctx.fillRect(160, 8, 10, 3);
  ctx.fillStyle = 'rgba(255,255,255,0.5)'; ctx.fillText('Лев', 173, 13);
  ctx.fillStyle = '#3498db'; ctx.fillRect(160, 16, 10, 3);
  ctx.fillStyle = 'rgba(255,255,255,0.5)'; ctx.fillText('Прав', 173, 21);
}

function drawHist() {
  const ctx = histCanvas.value.getContext('2d');
  ctx.clearRect(0, 0, 220, 160);
  ctx.fillStyle = '#0f1525';
  ctx.fillRect(0, 0, 220, 160);

  const bins = 14;
  const maxSpeed = 10;
  const lHist = new Array(bins).fill(0);
  const rHist = new Array(bins).fill(0);

  for (const m of leftMols) {
    const s = Math.sqrt(m.vx * m.vx + m.vy * m.vy);
    const idx = Math.min(bins - 1, Math.floor(s / maxSpeed * bins));
    lHist[idx]++;
  }
  for (const m of rightMols) {
    const s = Math.sqrt(m.vx * m.vx + m.vy * m.vy);
    const idx = Math.min(bins - 1, Math.floor(s / maxSpeed * bins));
    rHist[idx]++;
  }

  const maxCount = Math.max(1, ...lHist, ...rHist);
  const barW = 170 / bins;
  const baseX = 30;
  const baseY = 140;

  // Оси
  ctx.strokeStyle = 'rgba(255,255,255,0.2)';
  ctx.beginPath(); ctx.moveTo(baseX, baseY); ctx.lineTo(baseX + 170, baseY); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(baseX, baseY); ctx.lineTo(baseX, 10); ctx.stroke();

  for (let i = 0; i < bins; i++) {
    const x = baseX + i * barW;
    const hL = (lHist[i] / maxCount) * 110;
    const hR = (rHist[i] / maxCount) * 110;

    if (hL > 0) {
      ctx.fillStyle = 'rgba(243,156,18,0.7)';
      ctx.fillRect(x + 1, baseY - hL, barW / 2 - 1, hL);
    }
    if (hR > 0) {
      ctx.fillStyle = 'rgba(52,152,219,0.7)';
      ctx.fillRect(x + barW / 2, baseY - hR, barW / 2 - 1, hR);
    }
  }

  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.font = '10px sans-serif';
  ctx.fillText('v', baseX + 172, 147);
  ctx.fillText('N', 10, 15);
}

/* ===================== АНИМАЦИЯ ===================== */
function toggleRun() {
  running.value = !running.value;
  if (running.value) loop();
  else cancelAnimationFrame(animId);
}

function loop() {
  if (!running.value) return;
  updatePhysics();
  draw();
  drawPV();
  drawHist();
  tick.value++;
  frameCount++;
  animId = requestAnimationFrame(loop);
}

onMounted(() => {
  initCanvas();
  resetAll();
  running.value = true;
  loop();
  window.addEventListener('resize', () => {
    initCanvas();
    piston.x = Math.min(Math.max(piston.x, 80), canvasW - 80);
  });
});

onUnmounted(() => {
  running.value = false;
  cancelAnimationFrame(animId);
});
</script>

<style scoped>
.thermo-page {
  padding: 10px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background: #0d1117;
  color: #c9d1d9;
  min-height: 100vh;
}

.controls-row {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.control-card {
  flex: 1;
  min-width: 220px;
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 10px;
  padding: 14px;
}

.control-card h3 {
  margin: 0 0 10px 0;
  font-size: 15px;
  color: #58a6ff;
  border-bottom: 1px solid #30363d;
  padding-bottom: 6px;
}

.control-card label {
  display: block;
  margin: 8px 0 4px;
  font-size: 12px;
  color: #8b949e;
}

.control-card select,
.control-card input[type="number"] {
  width: 100%;
  padding: 6px 8px;
  background: #0d1117;
  border: 1px solid #30363d;
  border-radius: 6px;
  color: #c9d1d9;
  font-size: 13px;
}

.btn-row {
  display: flex;
  gap: 6px;
  margin-top: 6px;
}

.control-card button {
  flex: 1;
  padding: 6px 8px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  background: #21262d;
  color: #c9d1d9;
  border: 1px solid #30363d;
  transition: all 0.15s;
}

.control-card button:hover {
  background: #30363d;
}

.control-card button.active {
  background: #1f6feb;
  color: white;
  border-color: #388bfd;
}

.heat-btn { background: #3d1f1f !important; color: #ff7b72 !important; border-color: #8b3a3a !important; }
.heat-btn:hover { background: #5a2e2e !important; }
.cool-btn { background: #1f2d3d !important; color: #79c0ff !important; border-color: #3a5a8b !important; }
.cool-btn:hover { background: #2e3e5a !important; }
.run-btn { background: #1f4420 !important; color: #7ee787 !important; }
.run-btn:hover { background: #2e6b30 !important; }
.reset-btn { background: #3d2a1f !important; color: #ffa657 !important; }
.reset-btn:hover { background: #5a402e !important; }

.piston-card .global-controls {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #30363d;
}

.small-label {
  display: flex !important;
  align-items: center;
  gap: 6px;
  font-size: 11px !important;
  margin-top: 8px !important;
}

.canvas-wrapper {
  width: 100%;
  border: 1px solid #30363d;
  border-radius: 10px;
  overflow: hidden;
  background: #0b0f1a;
  margin-bottom: 12px;
}

.canvas-wrapper canvas {
  display: block;
  width: 100%;
}

.stats-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.stat-card, .graph-card {
  flex: 1;
  min-width: 180px;
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 10px;
  padding: 12px;
}

.stat-card h4, .graph-card h4 {
  margin: 0 0 8px 0;
  font-size: 13px;
  color: #58a6ff;
}

.stat-line {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  padding: 3px 0;
  border-bottom: 1px solid #21262d;
}

.stat-line .label { color: #8b949e; }
.stat-line .value { color: #c9d1d9; font-weight: 600; }

.graph-card canvas {
  display: block;
  width: 100%;
  border-radius: 6px;
}

.theory-box {
  margin-top: 12px;
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 10px;
  padding: 12px 16px;
}

.theory-box h4 {
  margin: 0 0 6px 0;
  color: #58a6ff;
  font-size: 13px;
}

.theory-box p {
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  color: #8b949e;
}

.theory-box em {
  color: #c9d1d9;
  font-style: normal;
  background: #21262d;
  padding: 1px 4px;
  border-radius: 4px;
}
</style>
